/**
 * Loads which organisations the signed-in person is in and which is active,
 * and provides it through OrgsContext (src/lib/useOrgs.ts — read it with
 * useOrgs()). Component-only file on purpose; see the note in useOrgs.ts.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useAuth } from '@clerk/clerk-react'
import { FEATURE_ORGANISATIONS } from '@/lib/featureFlags'
import { orgApi, type CodeJoin, type MyOrg, type PendingTransfer, type WaitingInvite } from '@/lib/orgApi'
import { useNotifications } from '@/lib/notifications'
import { setActiveOrgId } from '@/lib/tenant'
import { OrgsContext, PENDING_ORG_CODE_KEY, PENDING_ORG_INVITE_KEY, type OrgsState } from '@/lib/useOrgs'
import { RequestSent } from './RequestSent'

const readPendingCode = () => { try { return sessionStorage.getItem(PENDING_ORG_CODE_KEY) } catch { return null } }
const clearPendingCode = () => { try { sessionStorage.removeItem(PENDING_ORG_CODE_KEY) } catch { /* private mode */ } }
/** What to say after a code was used — they joined (invited), or the admins were asked. */
function codeToast(j: CodeJoin) {
  return j.requested
    ? { status: 'info' as const, title: `Request sent to ${j.org.name}`, body: 'An admin needs to approve it. You’ll get an email when they do.' }
    : { status: 'success' as const, title: `Welcome to ${j.org.name}`, body: 'You were invited, so you’re in straight away.' }
}
/** The four counts "What's new" shows, from bootstrap's `adopted`; null when nothing moved. */
function adoptionCounts(a: Record<string, number> | null | undefined): OrgsState['adoptedNow'] {
  if (!a) return null
  const c = { courses: a.courses ?? 0, images: a.images ?? 0, videos: a.videos ?? 0, pdfs: a.pdfs ?? 0 }
  return c.courses + c.images + c.videos + c.pdfs > 0 ? c : null
}
const hasPendingInvite = () => { try { return !!sessionStorage.getItem(PENDING_ORG_INVITE_KEY) } catch { return false } }

export function OrgProvider({ children }: { children: ReactNode }) {
  const { isLoaded, userId } = useAuth()
  const { toast, notify, setInviteActions, setInviteResponder, setTransferResponder, refresh } = useNotifications()
  const [status, setStatus] = useState<OrgsState['status']>(FEATURE_ORGANISATIONS ? 'loading' : 'off')
  const [error, setError] = useState<string | null>(null)
  const [orgs, setOrgs] = useState<MyOrg[]>([])
  const [activeId, setActiveId] = useState<string | null>(null)
  const [justCreated, setJustCreated] = useState(false)
  /** Content moved on this sign-in (see OrgsState.adoptedNow) — kept from the FIRST bootstrap of the
   *  load: a later one in the same load moves nothing and would report null. */
  const [adoptedNow, setAdoptedNow] = useState<OrgsState['adoptedNow']>(null)
  const [paused, setPaused] = useState<Array<{ id: string; name: string }>>([])
  const [removed, setRemoved] = useState<Array<{ id: string; name: string }>>([])
  const [requested, setRequested] = useState<Array<{ id: string; name: string }>>([])
  /** Signed in through a code they weren't invited with, and they have an organisation of their own:
   *  the organisation the request went to — a page says so before they go on (24 Oct 2026). */
  const [requestSentTo, setRequestSentTo] = useState<string | null>(null)
  const [transfers, setTransfers] = useState<PendingTransfer[]>([])
  const [invites, setInvites] = useState<WaitingInvite[]>([])
  const [invitedFirst, setInvitedFirst] = useState(false)
  const loadedFor = useRef<string | null>(null)
  // Signed out (or a different person signed in): drop the last person's organisations at once, so
  // the next one never sees them for a render while their own load is on its way (code review, 16 Oct).
  const [shownFor, setShownFor] = useState<string | null | undefined>(undefined)
  if (isLoaded && (userId ?? null) !== shownFor) {
    setShownFor(userId ?? null)
    if (shownFor !== undefined) {
      setOrgs([]); setActiveId(null); setPaused([]); setRemoved([]); setRequested([]); setTransfers([]); setInvites([])
      setJustCreated(false); setInvitedFirst(false); setError(null)
      if (FEATURE_ORGANISATIONS) setStatus('loading')
    }
  }
  const loadRef = useRef<() => Promise<void>>(async () => {})

  /**
   * Invitations waiting for this person's addresses are in the bell with Join / Decline (1 Oct 2026)
   * — whoever signed up at /sign-up instead of through the email's link would otherwise never see
   * them. Since 14 Oct 2026 the notification itself is on the SERVER (bootstrap makes it, once, and
   * takes it off once the invitation is no longer waiting), so read and dismissed hold in every
   * browser. This only supplies the buttons, then asks the bell for the server's list.
   */
  /** Join / Decline for one invitation — from the bootstrap list, or one that arrived later (D3). */
  const inviteButtons = useCallback((orgId: string, inviteId: string, orgName?: string) => {
    const answer = async (accept: boolean) => {
      try {
        const r = await orgApi.respondInvite(orgId, inviteId, accept)   // the server takes its notification off
        setInviteActions(prev => { const { [inviteId]: _gone, ...rest } = prev; void _gone; return rest })
        refresh()
        toast({ status: 'success', title: accept ? `Welcome to ${r.org?.name ?? orgName ?? 'the organisation'}` : 'Invitation declined' })
        if (accept) await loadRef.current()        // the server already made it the active organisation
      } catch (e) {
        toast({ status: 'error', title: 'Couldn’t answer the invitation', body: (e as Error).message })
      }
    }
    return [{ label: 'Join', onClick: (): void => void answer(true), variant: 'primary' as const }, { label: 'Decline', onClick: (): void => void answer(false), variant: 'ghost' as const }]
  }, [setInviteActions, refresh, toast])
  useEffect(() => {
    setInviteResponder((orgId, inviteId) => inviteButtons(orgId, inviteId))
    return () => setInviteResponder(null)
  }, [setInviteResponder, inviteButtons])

  // Accept / Decline on an ownership transfer's bell notice (20 Oct 2026) — the same answer the
  // banner gives, so it works without reloading the app first.
  useEffect(() => {
    setTransferResponder(transferId => {
      const answer = async (accept: boolean) => {
        try {
          await orgApi.respondTransfer(transferId, accept)   // the server takes the notice off
          refresh()
          toast({ status: 'success', title: accept ? 'You’re now the Owner' : 'Request declined' })
          await loadRef.current()
        } catch (e) {
          toast({ status: 'error', title: 'Couldn’t answer the request', body: (e as Error).message })
        }
      }
      return [{ label: 'Accept', onClick: (): void => void answer(true), variant: 'primary' as const }, { label: 'Decline', onClick: (): void => void answer(false), variant: 'ghost' as const }]
    })
    return () => setTransferResponder(null)
  }, [setTransferResponder, refresh, toast])

  const showInvites = useCallback((list: WaitingInvite[]) => {
    const byInvite: Record<string, ReturnType<typeof inviteButtons>> = {}
    for (const inv of list) byInvite[inv.id] = inviteButtons(inv.orgId, inv.id, inv.orgName)
    setInviteActions(() => byInvite)
    refresh()
  }, [setInviteActions, refresh, inviteButtons])

  const load = useCallback(async () => {
    setStatus('loading'); setError(null)
    try {
      const pending = readPendingCode()
      let r = await orgApi.bootstrap({ joining: !!pending || hasPendingInvite() })
      const first = r
      let orgsNow = r.orgs, active = r.activeOrgId
      if (pending) {
        clearPendingCode()
        try {
          // Invited → they're in. Otherwise the code sends the admins a request (15, 16 Oct 2026);
          // someone new then has no organisation yet and the app says they're waiting (NoAccess).
          const j = await orgApi.joinCode(pending)
          r = await orgApi.bootstrap({ joining: true })
          orgsNow = r.orgs; active = j.joined ? j.org.id : r.activeOrgId
          // A request, with somewhere of their own to go: a page, not a toast that is gone in seconds.
          // With nowhere to go, NoAccess shows "Waiting for X to approve you" instead.
          if (j.requested && orgsNow.length) setRequestSentTo(j.org.name)
          else if (!j.requested) toast(codeToast(j))
        } catch (e) {
          toast({ status: 'error', title: "Couldn't join with that code", body: (e as Error).message })
          // The code didn't work (turned off, regenerated, mistyped): load again as an ordinary
          // sign-in, so someone new gets their own organisation and setup rather than none at all
          // (C5 / C6, 16 Oct 2026). Someone already in an organisation just lands there.
          r = await orgApi.bootstrap({ joining: hasPendingInvite() })
          orgsNow = r.orgs; active = r.activeOrgId
        }
      }
      // My learning, started now (24 Oct 2026): Home's Continue / Due and a course page's "You're
      // taking this" waited a second for it after everything else had drawn.
      if (active) void orgApi.myLearning(active).catch(() => {})
      // Their own work was moved in on this sign-in: "What's new" instead of setup (25 Oct 2026) —
      // setup is for someone starting from nothing.
      const moved = adoptionCounts(first.adopted)
      if (moved) setAdoptedNow(moved)
      setOrgs(orgsNow); setActiveId(active); setJustCreated(r.created && !moved); setPaused(r.paused); setRemoved(r.removed ?? []); setRequested(r.requested ?? [])
      // Paused somewhere but still in other organisations (G2): say so once a session, and keep it
      // in the bell. With nothing else left, the app shows a page instead (AppShell → NoAccess).
      if (orgsNow.length) for (const p of r.paused) {
        const key = `lb_paused_told_${p.id}`
        let told = false
        try { told = !!sessionStorage.getItem(key); sessionStorage.setItem(key, '1') } catch { /* private mode: tell anyway */ }
        if (told) continue
        const body = 'Ask one of its admins to turn it back on. Your progress and details are kept.'
        notify({ id: `paused-${p.id}`, category: 'info', status: 'info', title: `Your access to ${p.name} is paused`, body })
        toast({ status: 'info', title: `Your access to ${p.name} is paused`, body })
      }
      setTransfers(r.transfers ?? [])
      setInvites(r.invites ?? []); setInvitedFirst(!!r.invitedFirst)
      showInvites(r.invites ?? [])
      setStatus('ready')
    } catch (e) {
      setError((e as Error).message || "Couldn't load your organisations.")
      setStatus('error')
    }
  }, [toast, notify, showInvites])
  useEffect(() => { loadRef.current = load }, [load])

  useEffect(() => {
    // Signed out: forget who was loaded, so signing back in — even as the same person, in the same
    // tab — loads again and picks up a code or invitation saved meanwhile (G6, 16 Oct 2026).
    if (isLoaded && !userId) { loadedFor.current = null; return }
    if (!FEATURE_ORGANISATIONS || !isLoaded || !userId) return
    if (loadedFor.current === userId) return
    loadedFor.current = userId
    void load()
  }, [isLoaded, userId, load])

  const switchTo = useCallback(async (orgId: string) => {
    const target = orgs.find(o => o.id === orgId)
    if (!target || orgId === activeId) return
    const before = activeId
    setActiveId(orgId)                                   // instant; the server remembers it
    try {
      await orgApi.setActive(orgId)
      toast({ status: 'success', title: `Switched to ${target.name}` })
    } catch (e) {
      setActiveId(before)
      toast({ status: 'error', title: "Couldn't switch organisation", body: (e as Error).message })
    }
  }, [orgs, activeId, toast])

  const joinWithCode = useCallback(async (code: string) => {
    const j = await orgApi.joinCode(code)
    const r = await orgApi.bootstrap({ joining: true })
    // Only a real join switches organisation; a request leaves them where they are.
    setOrgs(r.orgs); setActiveId(prev => (j.joined ? j.org.id : (r.orgs.some(o => o.id === prev) ? prev : r.activeOrgId)))
    setPaused(r.paused); setRemoved(r.removed ?? []); setRequested(r.requested ?? [])
    return j
  }, [])

  const markSetupDone = useCallback(() => setJustCreated(false), [])
  const markWhatsNewSeen = useCallback(() => setAdoptedNow(null), [])

  /** From /org/invited: they want their own organisation — make it, then setup takes over. */
  const createOwnOrg = useCallback(async () => {
    const r = await orgApi.bootstrap({ createOwn: true })
    await load()
    // The reload's bootstrap creates nothing, so it reports created=false and would cancel setup
    // (B12, 16 Oct 2026 — they landed on Home). The first call is the one that made the organisation.
    if (r.created) setJustCreated(true)
  }, [load])

  const patchCurrent = useCallback((p: Partial<MyOrg>) => {
    setOrgs(list => list.map(o => (o.id === activeId ? { ...o, ...p } : o)))
  }, [activeId])

  // Tell the data layer which organisation new courses go to and the list shows.
  const currentOrg = orgs.find(o => o.id === activeId) ?? null
  const currentId = currentOrg?.id ?? null
  const currentOwned = !!currentOrg?.roles.includes('owner')
  // Recorded DURING render, not in an effect: React runs a page's own effects
  // before its parent's, so an effect here let the freshly re-opened page fetch
  // with the PREVIOUS organisation — the "wrong organisation's content after
  // switching" glitch (30 Sep). The assignment is idempotent, so a repeated
  // render is harmless.
  setActiveOrgId(FEATURE_ORGANISATIONS && userId ? currentId : null, currentOwned, userId ?? null)

  const value = useMemo<OrgsState>(() => {
    const current = orgs.find(o => o.id === activeId) ?? null
    const roles = current?.roles ?? []
    const isManager = roles.includes('owner') || roles.includes('admin')
    // No organisation loaded (flag off, still loading, or an error): behave as
    // today — a creator — so no page is ever hidden by a failed request.
    const isCreator = !current || isManager || roles.includes('creator')
    return {
      status, error, orgs, current, roles, isManager, isCreator,
      isLearnerOnly: !!current && !isCreator,
      ownsOne: orgs.some(o => o.roles.includes('owner')),
      justCreated, markSetupDone, paused, removed, requested, transfers, invites, invitedFirst, adoptedNow, markWhatsNewSeen, createOwnOrg,
      switchTo, joinWithCode, reload: load, patchCurrent,
    }
  }, [status, error, orgs, activeId, justCreated, markSetupDone, paused, removed, requested, transfers, invites, invitedFirst, adoptedNow, markWhatsNewSeen, createOwnOrg, switchTo, joinWithCode, load, patchCurrent])

  return <OrgsContext.Provider value={value}>
    {requestSentTo ? <RequestSent orgName={requestSentTo} onContinue={() => setRequestSentTo(null)} /> : children}
  </OrgsContext.Provider>
}

